import React, { TouchableOpacity, View } from 'react-native';
import MyDataTable from '../../../components/MyDataTable';
import { translate } from '../../../services/translate';
import { Pagination } from '../../../components/Pagination';
import { RouteProp, useFocusEffect, useIsFocused, useRoute } from '@react-navigation/native';
import { AccessParamList } from '../../../typings/Params';
import { useCallback, useState } from 'react';
import useFilters from '../../../components/Filter/hooks/useFilters';
import { platformSettingsService } from '../../../services/central-api/platform-settings';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import EventIntegrationsModal from './EventIntegrationModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/free-solid-svg-icons';
import { RootNavigation } from '../../../Routes';

export default function EventIntegrationTable({ navigation, authenticatedUser }: { navigation: RootNavigation; authenticatedUser: AuthenticatedUser | undefined | null; }) {
    const { styles, theme } = useStyles(styleSheet);
    const route = useRoute<RouteProp<AccessParamList, 'PlatformSettings'>>();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isModalVisible, setModalVisible] = useState<boolean>(false);
    const [selectedEventIntegration, setSelectedEventIntegration] = useState<EventsIntegration>();
    const [eventsIntegration, setEventsIntegration] = useState<EventsIntegration[]>([]);
    const [totalItems, setTotalItems] = useState(0);
    const [filters, setFilters] = useFilters<AccessParamList['PlatformSettings']>({
        ...route.params,
        page: Math.max(route.params.page - 1, 0),
        limit: route.params.limit ?? 25,
    });

    async function getEventsIntegration() {
        try {
            setIsLoading(true);
            const eventsIntegration = await platformSettingsService.getPaginatedEventsIntegration(filters);
            setEventsIntegration(eventsIntegration.rows);
            setTotalItems(eventsIntegration.count);
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }

    function handleFormClose() {
        setSelectedEventIntegration(undefined);
        setModalVisible(false);
        getEventsIntegration();
    }

    const isFocused = useIsFocused();
    useFocusEffect(useCallback(() => {
        if (isFocused) {
            getEventsIntegration();
        }
    }, [isFocused, navigation]));

    return (
        <View style={{ flex: 1 }}>
            <View style={styles.eventsTable}>
                <MyDataTable
                    columns={[
                        {
                            name: 'ID',
                            selector: row => row.id,
                            grow: 0
                        },
                        {
                            name: translate('name'),
                            selector: row => translate(`${row.triggerType}`),
                            wrap: true,
                            grow: 2,
                        },
                        {
                            name: translate('actingBody'),
                            selector: row => row.actingBody?.name ?? '',
                            wrap: true,
                            grow: 1,
                        },
                        {
                            name: translate('occurrenceType'),
                            selector: row => row.occurrenceType?.name ?? '',
                            wrap: true,
                            grow: 1,
                        },
                        {
                            cell: row => (
                                <View style={{ flexDirection: 'row', columnGap: 10 }}>
                                    <TouchableOpacity onPress={() => {
                                        setSelectedEventIntegration(row);
                                        setModalVisible(true);
                                    }}>
                                        <FontAwesomeIcon icon={faEdit} fontSize={16} color={theme.colors.iconColor} />
                                    </TouchableOpacity>
                                </View>
                            ),
                            wrap: true,
                            button: true
                        }
                    ]}
                    data={eventsIntegration}
                    progressPending={isLoading}
                />
            </View>
            <Pagination
                totalItems={totalItems}
                currentPage={filters.page}
                pageSize={filters.limit}
                setPageSize={pageSize => {
                    setFilters(old => ({
                        ...old,
                        limit: pageSize,
                        page: 0,
                    }));

                    navigation.setParams({
                        ...route.params,
                        limit: pageSize,
                        page: 1,
                    });
                }}
                setPage={page => {
                    setFilters(old => ({
                        ...old,
                        page
                    }));

                    navigation.setParams({
                        ...route.params,
                        page: page + 1
                    });
                }}
            />
            <EventIntegrationsModal
                onClose={handleFormClose}
                isModalVisible={isModalVisible}
                eventIntegration={selectedEventIntegration}
                authenticatedUser={authenticatedUser} />
        </View>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    eventsTable: {
        borderWidth: 1,
        borderTopLeftRadius: 8,
        borderTopRightRadius: 8,
        borderColor: theme.colors.dataTable.border,
        backgroundColor: theme.colors.dataTable.background,
        paddingHorizontal: 10,
        flex: 1,
    },
}));
